import React, { useEffect, useState } from "react";

import { Button, Checkbox, LabeledField, Modal, Stack, TextInput } from "shared/ui";
import {
    MAX_REPORTING_BOT_ID,
    TELEGRAM_REPORTING_BOT_ID,
} from "services/menu/restModels";
import {
    MenuChatDto,
    MenuChatMessengerDto,
    ReportingFilterDto,
} from "services/menu/restModels";
import { OrderStatusDto } from "services/orders/restModels";

import { useMenuSettingsContext } from "features/MenuSettings/context";

import { Instruction, InstructionStep, InstructionWarning } from "../styles";

const BOT_ID_BY_MESSENGER: Record<MenuChatMessengerDto, number> = {
    Telegram: TELEGRAM_REPORTING_BOT_ID,
    Max: MAX_REPORTING_BOT_ID,
};

const ORDER_STATUSES_FILTER: ReportingFilterDto = {
    type: "WhitelistedOrderStatuses",
    orderStatuses: [OrderStatusDto.new],
};

const WAITER_FILTER: ReportingFilterDto = { type: "CallWaiterForTable" };

const hasFilter = (filters: ReportingFilterDto[], type: string) =>
    filters.some((item) => item.type === type);

export interface ChatDialogTarget {
    messenger: MenuChatMessengerDto;
    editIndex?: number;
}

interface ChatDialogProps {
    target: ChatDialogTarget | null;
    onClose: () => void;
}

export const ChatDialog = ({ target, onClose }: ChatDialogProps) => {
    const { chats } = useMenuSettingsContext();

    const [chatIdText, setChatIdText] = useState<string>("");
    const [chatTitle, setChatTitle] = useState<string>("");
    const [notifyOrders, setNotifyOrders] = useState<boolean>(true);
    const [notifyWaiter, setNotifyWaiter] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const isEdit = !!target && target.editIndex !== undefined;

    useEffect(() => {
        if (!target) return;

        setError(null);

        const existing = isEdit ? chats.chats[target.editIndex] : null;

        setChatIdText(existing ? String(existing.chatId) : "");
        setChatTitle(existing ? existing.chatTitle || "" : "");
        setNotifyOrders(
            existing ? hasFilter(existing.filters, "WhitelistedOrderStatuses") : true
        );
        setNotifyWaiter(existing ? hasFilter(existing.filters, "CallWaiterForTable") : false);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- инициализация при открытии
    }, [target]);

    if (!target) return null;

    const isTelegram = target.messenger === "Telegram";

    const trimmedId = chatIdText.trim();
    const trimmedTitle = chatTitle.trim();
    // Оба поля обязательные: пока пусто — кнопка недоступна, формат проверяем при отправке.
    const isFilled = trimmedId !== "" && trimmedTitle !== "";

    const handleSubmit = () => {
        const chatId = Number(trimmedId);
        if (!Number.isSafeInteger(chatId)) {
            setError("Идентификатор чата — целое число");
            return;
        }

        const filters: ReportingFilterDto[] = [];
        if (notifyOrders) filters.push(ORDER_STATUSES_FILTER);
        if (notifyWaiter) filters.push(WAITER_FILTER);

        const chat: MenuChatDto = {
            messenger: target.messenger,
            chatId,
            chatTitle: trimmedTitle,
            botId: BOT_ID_BY_MESSENGER[target.messenger],
            filters,
        };

        if (chats.isChatDuplicate(chat, target.editIndex)) {
            setError("Такой чат уже добавлен");
            return;
        }

        if (isEdit) {
            chats.updateChat(target.editIndex, chat);
        } else {
            chats.addChat(chat);
        }

        onClose();
    };

    return (
        <Modal
            open
            onClose={onClose}
            title={`${isEdit ? "Изменить" : "Добавить"} чат ${
                isTelegram ? "Telegram" : "MAX"
            }`}
            actions={
                <>
                    <Button variant="outlined" color="neutral" onClick={onClose}>
                        Отмена
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!isFilled}
                        onClick={handleSubmit}
                    >
                        Сохранить
                    </Button>
                </>
            }
        >
            {isTelegram ? (
                <Instruction>
                    <InstructionStep>1. Создайте группу в Telegram.</InstructionStep>
                    <InstructionStep>
                        2. Добавьте бота{" "}
                        <a href="https://t.me/RestifyOneBot" target="_blank" rel="noreferrer">
                            RestifyOneBot
                        </a>{" "}
                        и назначьте его администратором.
                    </InstructionStep>
                    <InstructionStep>3. Напишите в группу любое сообщение.</InstructionStep>
                    <InstructionStep>
                        4. Скопируйте идентификатор группы из ссылки на сообщение.
                    </InstructionStep>
                    <InstructionWarning>
                        Важно: к идентификатору нужно добавить «-100» в начале — без этого
                        сообщения не придут.
                    </InstructionWarning>
                </Instruction>
            ) : (
                <Instruction>
                    <InstructionStep>1. Создайте группу в мессенджере MAX.</InstructionStep>
                    <InstructionStep>2. Напишите в неё любое сообщение.</InstructionStep>
                    <InstructionStep>
                        3. Перешлите это сообщение в бота, например{" "}
                        <a
                            href="https://max.ru/id262310199016_1_bot"
                            target="_blank"
                            rel="noreferrer"
                        >
                            LeadConverterBot
                        </a>{" "}
                        — в ответ он пришлёт ID чата и его тип.
                    </InstructionStep>
                    <InstructionStep>
                        4. Перейдите в{" "}
                        <a
                            href="https://max.ru/id1615013581_bot"
                            target="_blank"
                            rel="noreferrer"
                        >
                            RestifyBot
                        </a>
                        , нажмите «Начать» и добавьте бота администратором в группу.
                    </InstructionStep>
                    <InstructionWarning>
                        Важно: ID группы или канала копируйте вместе со знаком «−» в начале —
                        без него сообщения не придут.
                    </InstructionWarning>
                </Instruction>
            )}

            <LabeledField label="Идентификатор чата">
                <TextInput value={chatIdText} onChange={setChatIdText} />
            </LabeledField>

            <LabeledField label="Название чата" hint={error}>
                <TextInput
                    value={chatTitle}
                    onChange={setChatTitle}
                    placeholder="Например, заказы"
                />
            </LabeledField>

            <Stack>
                <Checkbox
                    checked={notifyOrders}
                    label="О принятых заказах"
                    onChange={setNotifyOrders}
                />
                <Checkbox
                    checked={notifyWaiter}
                    label="О вызове персонала"
                    onChange={setNotifyWaiter}
                />
            </Stack>
        </Modal>
    );
};
